import { computed, unref } from 'vue';
import {
  mapReadinessChecks,
  readAssistantReadiness,
  readinessPercentage,
} from './readiness';
import { buildAgentPreview } from './preview';
import { buildAgentSourceCards } from './sourceCards';
import { buildRecentActivityItems } from './useAgentActivity';
import {
  presentAgentHealth,
  presentAgentLifecycle,
  readAgentHealthStatus,
  readAgentLifecycle,
  readAgentLifecycleStatus,
} from './agentStatus';

export const isRecord = value =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const useAssistantManagement = ({ assistant, currentConfig }) => {
  const assistantManagement = computed(() => {
    const currentAssistant = unref(assistant) || {};
    const config = unref(currentConfig) || {};
    const candidates = [
      config.management,
      config.agent_configuration,
      currentAssistant.agent_configuration,
      currentAssistant.management?.agent_configuration,
      currentAssistant.management,
    ].filter(isRecord);

    return Object.assign({}, ...candidates);
  });
  const brandVoiceManagement = computed(
    () => assistantManagement.value.brand_voice || {}
  );

  return { assistantManagement, brandVoiceManagement };
};

export function useAgentWorkspacePresentation({
  route,
  assistant,
  assistantManagement,
  identityForm,
  dirtySectionState,
  runs,
  runsMeta,
  memories,
  pendingApprovals,
  scenarios,
  availableActions,
  memoriesTotalCount,
  completedRunsCount,
  failedRunsCount,
  handoffRunsCount,
  canManage,
  managementEnabled,
  approvalsEnabled,
  toolsEnabled,
  t,
  hasTranslation,
}) {
  const workspaceSections = computed(() =>
    [
      {
        id: 'overview',
        label: t('CAPTAIN_EXPERIENCE.WORKSPACE.NAV_OVERVIEW'),
        helper: t('CAPTAIN_EXPERIENCE.WORKSPACE.NAV_OVERVIEW_HELPER'),
        icon: 'i-lucide-layout-dashboard',
      },
      {
        id: 'identity',
        label: t('CAPTAIN_EXPERIENCE.WORKSPACE.NAV_IDENTITY'),
        helper: t('CAPTAIN_EXPERIENCE.WORKSPACE.NAV_IDENTITY_HELPER'),
        icon: 'i-lucide-bot',
      },
      {
        id: 'voice',
        label: t('CAPTAIN_EXPERIENCE.SCRIPT.CHECKLIST_VOICE_LABEL'),
        helper: t('CAPTAIN_EXPERIENCE.SCRIPT.CHECKLIST_VOICE_HELPER'),
        icon: 'i-lucide-mic-vocal',
        available: unref(canManage),
      },
      {
        id: 'knowledge',
        label: t('CAPTAIN_EXPERIENCE.WORKSPACE.NAV_KNOWLEDGE'),
        helper: t('CAPTAIN_EXPERIENCE.WORKSPACE.NAV_KNOWLEDGE_HELPER'),
        icon: 'i-lucide-library-big',
      },
      {
        id: 'handoff',
        label: t('CAPTAIN_EXPERIENCE.WORKSPACE.NAV_HANDOFF'),
        helper: t('CAPTAIN_EXPERIENCE.WORKSPACE.NAV_HANDOFF_HELPER'),
        icon: 'i-lucide-user-round-plus',
      },
      {
        id: 'publication',
        label: t('CAPTAIN_EXPERIENCE.WORKSPACE.NAV_PUBLICATION'),
        helper: t('CAPTAIN_EXPERIENCE.WORKSPACE.NAV_PUBLICATION_HELPER'),
        icon: 'i-lucide-rocket',
        available: unref(managementEnabled) && unref(canManage),
      },
      {
        id: 'activity',
        label: t('CAPTAIN_EXPERIENCE.WORKSPACE.NAV_ACTIVITY'),
        helper: t('CAPTAIN_EXPERIENCE.WORKSPACE.NAV_ACTIVITY_HELPER'),
        icon: 'i-lucide-activity',
        available: unref(approvalsEnabled) && unref(canManage),
      },
      {
        id: 'operations',
        label: t('CAPTAIN_EXPERIENCE.WORKSPACE.NAV_OPERATIONS'),
        helper: t('CAPTAIN_EXPERIENCE.WORKSPACE.NAV_OPERATIONS_HELPER'),
        icon: 'i-lucide-gauge',
        available: unref(approvalsEnabled) && unref(canManage),
      },
      {
        id: 'test',
        label: t('CAPTAIN_EXPERIENCE.WORKSPACE.NAV_TEST'),
        helper: t('CAPTAIN_EXPERIENCE.WORKSPACE.NAV_TEST_HELPER'),
        icon: 'i-lucide-play',
        routeName: 'captain_assistants_playground_index',
        external: true,
        available: unref(canManage),
      },
    ]
      .filter(item => item.available !== false)
      .map(item => ({
        ...item,
        dirty: Boolean(dirtySectionState.value[item.id]),
      }))
  );

  const localWorkspaceSectionIds = computed(
    () =>
      new Set(
        workspaceSections.value
          .filter(item => !item.external)
          .map(item => item.id)
      )
  );
  const activeSection = computed(() => {
    const requestedSection = String(route.query.section || 'overview');
    return localWorkspaceSectionIds.value.has(requestedSection)
      ? requestedSection
      : 'overview';
  });

  const persistedTestRunsCount = computed(() => {
    const management = unref(assistantManagement);
    const runtime = isRecord(management.runtime) ? management.runtime : {};
    const summarizedCount = Number(
      runtime.playground_runs_count ??
        runtime.test_runs_count ??
        unref(runsMeta).source_counts?.playground
    );
    if (Number.isFinite(summarizedCount)) return summarizedCount;

    return unref(runs).filter(
      run => run.source === 'playground' && run.status === 'completed'
    ).length;
  });

  const selectedAssistantName = computed(
    () => unref(assistant)?.name || t('CAPTAIN_EXPERIENCE.AUTO.NOVO_AGENTE')
  );
  const selectedAssistantDescription = computed(
    () =>
      unref(assistant)?.description ||
      t('CAPTAIN_EXPERIENCE.SCRIPT.SELECTED_ASSISTANT_DESCRIPTION')
  );
  const deleteAssistantDescription = computed(() =>
    t('CAPTAIN_EXPERIENCE.SCRIPT.DELETE_ASSISTANT_DESCRIPTION', {
      name: selectedAssistantName.value,
    })
  );
  const recentActivityItems = computed(() =>
    buildRecentActivityItems({
      runs: unref(runs),
      memories: unref(memories),
      approvals: unref(pendingApprovals),
      assistant: unref(assistant),
      translate: t,
      hasTranslation,
    })
  );

  const sourceReadinessConfiguration = computed(() => {
    const management = unref(assistantManagement);
    const readiness = isRecord(management.readiness)
      ? management.readiness
      : {};
    const sources =
      readiness.sources ||
      readiness.knowledge_sources ||
      management.knowledge_sources ||
      {};
    return isRecord(sources) ? sources : {};
  });
  const managedSourceState = keys => {
    const sourceKey = keys.find(key =>
      Object.prototype.hasOwnProperty.call(
        sourceReadinessConfiguration.value,
        key
      )
    );
    if (!sourceKey) {
      return {
        ready: null,
        status: t('CAPTAIN_EXPERIENCE.SCRIPT.STATUS_MANAGE'),
      };
    }

    const source = sourceReadinessConfiguration.value[sourceKey];
    const status = isRecord(source) ? source.status : null;
    let ready = Boolean(source);
    if (isRecord(source)) {
      ready = source.ready ?? ['ready', 'active', 'completed'].includes(status);
    } else if (typeof source === 'number') {
      ready = source > 0;
    }
    let statusLabel = isRecord(source) ? source.label : null;
    if (!statusLabel) {
      statusLabel = ready
        ? t('CAPTAIN_EXPERIENCE.SCRIPT.STATUS_READY')
        : t('CAPTAIN_EXPERIENCE.SCRIPT.STATUS_CONFIGURE');
    }
    return {
      ready,
      status: statusLabel,
    };
  };
  const documentsSourceState = computed(() =>
    managedSourceState(['documents', 'document', 'files'])
  );
  const approvedResponsesSourceState = computed(() =>
    managedSourceState(['approved_responses', 'responses', 'faqs'])
  );
  const sourceCards = computed(() =>
    buildAgentSourceCards({
      t,
      featureFaq: identityForm.featureFaq,
      featureMemory: identityForm.featureMemory,
      approvedResponsesState: approvedResponsesSourceState.value,
      documentsState: documentsSourceState.value,
      memoriesTotalCount: unref(memoriesTotalCount),
      scenariosCount: unref(scenarios).length,
      availableActionsCount: unref(availableActions).length,
      hasAdvancedManagement: unref(managementEnabled),
      canManage: unref(canManage),
      hasCaptainTools: unref(toolsEnabled),
    })
  );

  const assistantReadiness = computed(() =>
    readAssistantReadiness(unref(assistantManagement))
  );
  const hasCurrentTestRun = computed(
    () => assistantReadiness.value.checks.tested === true
  );
  const readinessChecklist = computed(() =>
    mapReadinessChecks(assistantReadiness.value.checks, t, {
      blockers: assistantReadiness.value.blockers,
      warnings: assistantReadiness.value.warnings,
      testStatus: assistantReadiness.value.testStatus,
    })
  );
  const readinessPercent = computed(() =>
    readinessPercentage(assistantReadiness.value)
  );
  const assistantLifecycle = computed(() =>
    readAgentLifecycle(unref(assistantManagement))
  );
  const lifecycleStatus = computed(() =>
    readAgentLifecycleStatus(
      unref(assistantManagement),
      identityForm.publicationMode
    )
  );
  const lifecyclePresentation = computed(() =>
    presentAgentLifecycle(lifecycleStatus.value, t)
  );
  const healthStatus = computed(() =>
    readAgentHealthStatus(unref(assistantManagement), {
      failedRunsCount: unref(failedRunsCount),
      hasCurrentTestRun: hasCurrentTestRun.value,
    })
  );
  const healthPresentation = computed(() =>
    presentAgentHealth(healthStatus.value, t)
  );
  const readinessLabel = computed(() => {
    if (assistantReadiness.value.ready) {
      return t('CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_READY');
    }
    if (assistantReadiness.value.completed > 0) {
      return t('CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_IN_PROGRESS');
    }
    return t('CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_NEEDS_SETUP');
  });
  const readinessSummary = computed(() =>
    t('CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_SUMMARY', {
      completed: assistantReadiness.value.completed,
      total: assistantReadiness.value.total,
    })
  );

  const workspaceMetrics = computed(() =>
    [
      {
        label: t('CAPTAIN_EXPERIENCE.SCRIPT.METRIC_COMPLETED_RUNS'),
        value: unref(completedRunsCount),
        helper: t('CAPTAIN_EXPERIENCE.SCRIPT.METRIC_COMPLETED_RUNS_HELPER'),
        icon: 'i-lucide-circle-check-big',
        available: unref(approvalsEnabled),
      },
      {
        label: t('CAPTAIN_EXPERIENCE.SCRIPT.METRIC_HANDOFFS'),
        value: unref(handoffRunsCount),
        helper: t('CAPTAIN_EXPERIENCE.SCRIPT.METRIC_HANDOFFS_HELPER'),
        icon: 'i-lucide-user-round-plus',
        available: unref(approvalsEnabled),
      },
      {
        label: t('CAPTAIN_EXPERIENCE.SCRIPT.METRIC_AVAILABLE_ACTIONS'),
        value: unref(availableActions).length,
        helper: t('CAPTAIN_EXPERIENCE.SCRIPT.METRIC_AVAILABLE_ACTIONS_HELPER'),
        icon: 'i-lucide-wrench',
        available: unref(toolsEnabled) && unref(canManage),
      },
      {
        label: t('CAPTAIN_EXPERIENCE.WORKSPACE.METRIC_PERSISTED_TESTS'),
        value: persistedTestRunsCount.value,
        helper: t('CAPTAIN_EXPERIENCE.WORKSPACE.METRIC_PERSISTED_TESTS_HELPER'),
        icon: 'i-lucide-flask-conical',
        available: unref(approvalsEnabled),
      },
    ].filter(metric => metric.available !== false)
  );

  const improvementItems = computed(() => {
    const items = [];
    if (unref(approvalsEnabled) && unref(failedRunsCount) > 0) {
      items.push({
        title: t('CAPTAIN_EXPERIENCE.SCRIPT.IMPROVEMENT_REVIEW_FAILED_TITLE'),
        helper: t(
          'CAPTAIN_EXPERIENCE.SCRIPT.IMPROVEMENT_REVIEW_FAILED_HELPER',
          { count: unref(failedRunsCount) }
        ),
        icon: 'i-lucide-alert-triangle',
      });
    }
    if (
      unref(managementEnabled) &&
      unref(canManage) &&
      unref(scenarios).length === 0
    ) {
      items.push({
        title: t(
          'CAPTAIN_EXPERIENCE.SCRIPT.IMPROVEMENT_SAVE_INSTRUCTIONS_TITLE'
        ),
        helper: t(
          'CAPTAIN_EXPERIENCE.SCRIPT.IMPROVEMENT_SAVE_INSTRUCTIONS_HELPER'
        ),
        icon: 'i-lucide-message-square-text',
      });
    }
    if (!identityForm.featureFaq) {
      items.push({
        title: t('CAPTAIN_EXPERIENCE.SCRIPT.IMPROVEMENT_ENABLE_APPROVED_TITLE'),
        helper: t(
          'CAPTAIN_EXPERIENCE.SCRIPT.IMPROVEMENT_ENABLE_APPROVED_HELPER'
        ),
        icon: 'i-lucide-library',
      });
    }
    if (
      unref(managementEnabled) &&
      unref(canManage) &&
      unref(memoriesTotalCount) === 0
    ) {
      items.push({
        title: t('CAPTAIN_EXPERIENCE.SCRIPT.IMPROVEMENT_ADD_MEMORY_TITLE'),
        helper: t('CAPTAIN_EXPERIENCE.SCRIPT.IMPROVEMENT_ADD_MEMORY_HELPER'),
        icon: 'i-lucide-brain',
      });
    }
    if (
      unref(approvalsEnabled) &&
      unref(handoffRunsCount) > unref(completedRunsCount)
    ) {
      items.push({
        title: t('CAPTAIN_EXPERIENCE.AUTO.MELHORAR_TRANSFERENCIA_PARA_HUMANO'),
        helper: t('CAPTAIN_EXPERIENCE.SCRIPT.IMPROVEMENT_HANDOFF_HELPER'),
        icon: 'i-lucide-user-round-plus',
      });
    }
    return items.length
      ? items
      : [
          {
            title: t('CAPTAIN_EXPERIENCE.SCRIPT.IMPROVEMENT_WATCH_NEXT_TITLE'),
            helper: t(
              'CAPTAIN_EXPERIENCE.SCRIPT.IMPROVEMENT_WATCH_NEXT_HELPER'
            ),
            icon: 'i-lucide-sparkles',
          },
        ];
  });
  const previewResponse = computed(() => buildAgentPreview(identityForm, t));

  return {
    workspaceSections,
    localWorkspaceSectionIds,
    activeSection,
    selectedAssistantName,
    selectedAssistantDescription,
    deleteAssistantDescription,
    recentActivityItems,
    sourceCards,
    assistantReadiness,
    hasCurrentTestRun,
    readinessChecklist,
    readinessPercent,
    assistantLifecycle,
    lifecycleStatus,
    lifecyclePresentation,
    healthPresentation,
    readinessLabel,
    readinessSummary,
    workspaceMetrics,
    improvementItems,
    previewResponse,
  };
}
